import * as CL from '@design-system/component-library';
import { useState } from 'react';
import { t } from '../../common/i18n';
import { ToggleCheckboxCoCo } from '../ToggleCheckbox/ToggleCheckboxCoCo.js';
import type { ProductOfferingCartItems } from '../../common/types/shoppingBasketCoCo.js';
import type { PriceInfo } from '../ToggleCheckbox/ToggleCheckbox';
import type { BadgeProps } from '@design-system/component-library';
import type { ReactNode } from 'react';

export interface LinkInfo {
  id: string;
  linkInfoDescription?: string;
}

export interface ProductOfferAddOnProps {
  linkInfo?: LinkInfo;
  id: string;
  initialValue: boolean;
  badge?: BadgeProps;
  handleAddOnChange: (id: string, checked: boolean, productOffering?: ProductOfferingCartItems) => void;
  priceInfo?: PriceInfo;
  title: string;
  disabled?: boolean;
  productOffering?: ProductOfferingCartItems;
  hasQuantity?: boolean;
  quantity?: number;
  onQuantityChange?: (quantity: number) => void;
  /** When true, the quantity selector is stacked on top of the price instead of sitting in the middle. */
  quantityAbovePrice?: boolean;
  addonContent?: ReactNode;
}

interface ProductOfferAccordionInfoProps {
  id: string;
  description: string;
}

export const ProductOfferAccordionInfo = ({ id, description }: ProductOfferAccordionInfoProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <CL.Accordion className="of-voice-accordion" onOpen={() => setIsOpen(true)} onClose={() => setIsOpen(false)}>
      <CL.AccordionItem id={id} heading={isOpen ? t.ZBEP('Read less') : t.GJJF('Read more')} headingSize="s">
        <p>{description}</p>
      </CL.AccordionItem>
    </CL.Accordion>
  );
};

export const ProductOfferAddOn = (props: ProductOfferAddOnProps) => {
  const {
    linkInfo,
    id,
    initialValue = false,
    badge,
    title,
    handleAddOnChange,
    priceInfo,
    disabled = false,
    productOffering,
    hasQuantity,
    quantity = 1,
    onQuantityChange,
    quantityAbovePrice,
    addonContent,
  } = props;

  const [checked, setChecked] = useState(initialValue);

  const onChange = (isChecked: boolean) => {
    setChecked(isChecked);
    handleAddOnChange(id, isChecked, productOffering);
  };

  const quantitySelector = hasQuantity ? (
    <CL.Quantity
      minValue={1}
      currentValue={checked ? quantity : 1}
      onChange={onQuantityChange ?? (() => {})}
      disabled={hasQuantity}
    />
  ) : undefined;

  const additionalInformation = linkInfo?.linkInfoDescription ? (
    <ProductOfferAccordionInfo id={id} description={linkInfo.linkInfoDescription} />
  ) : (
    ''
  );

  return (
    <ToggleCheckboxCoCo
      onChange={onChange}
      title={title}
      badge={badge}
      initialValue={initialValue}
      additionalInformation={additionalInformation}
      priceInfo={priceInfo}
      disabled={disabled}
      quantitySelector={quantitySelector}
      additionalContent={addonContent}
      quantityAbovePrice={quantityAbovePrice}
    />
  );
};
